import { Customer } from "../../index.js";
import type { AsyncResourceVariant, CustomerDetailed } from "../../index.js";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  extractId,
  ReferenceModel,
} from "../../base";
import { provideReact } from "../../react/index.js";
import { DateTime } from "luxon";
import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound";
import { config } from "../../config/config";
import type {
  FinderProfileRequestData,
  FinderProfileRequestListItemData,
  FinderProfileRequestListModelQueryData,
  FinderProfileRequestStatus,
} from "./types";

export class FinderProfileRequest extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "leadfinder",
    "finderprofilerequest",
  );

  public static ofCustomer(customer: Customer | string): FinderProfileRequest {
    return new FinderProfileRequest(extractId(customer));
  }

  public static find = provideReact(
    async (
      customerId: string,
    ): Promise<FinderProfileRequestDetailed | undefined> => {
      const data = await config.behaviors.finderProfileRequest.find(customerId);

      if (data !== undefined) {
        return new FinderProfileRequestDetailed(data);
      }
    },
  );

  public static query() {
    return new FinderProfileRequestListQuery();
  }

  public static get = provideReact(
    async (customerId: string): Promise<FinderProfileRequestDetailed> => {
      const profile = await this.find(customerId);
      assertObjectFound(profile, "finderProfileRequest", customerId);
      return profile;
    },
  );

  public getDetailed = provideReact(async () => {
    if (this instanceof FinderProfileRequestDetailed) {
      return this;
    }
    return await FinderProfileRequest.get(this.id);
  }, [this.id]) as AsyncResourceVariant<
    () => Promise<FinderProfileRequestDetailed>
  >;

  public findDetailed = provideReact(() => {
    return FinderProfileRequest.find(this.id);
  }, [this.id]) as AsyncResourceVariant<
    () => Promise<FinderProfileRequestDetailed | undefined>
  >;

  public static async create(customerId: string) {
    const customer = await Customer.ofId(customerId).getDetailed();
    const contractPartnerDomain =
      this.getDomainFromContractPartnerEmail(customer);

    await config.behaviors.finderProfileRequest.create(customerId, {
      domain: contractPartnerDomain,
    });
  }

  private static getDomainFromContractPartnerEmail(
    customer: CustomerDetailed,
  ): string {
    const contractPartnerEmail = customer.data.owner?.emailAddress;

    if (!contractPartnerEmail) {
      throw new Error("Contract partner email is not defined");
    }

    return contractPartnerEmail.substring(
      (contractPartnerEmail as string).lastIndexOf("@") + 1,
    );
  }
}

export class FinderProfileRequestCommon extends classes(
  DataModel<FinderProfileRequestListItemData | FinderProfileRequestData>,
  FinderProfileRequest,
) {
  public readonly profileId: string;
  public readonly customerId: string;
  public readonly customer: Customer;
  public readonly domain: string;
  public readonly url: string;
  public readonly createdAt: DateTime;
  public readonly resultAt: DateTime | undefined;
  public readonly status: FinderProfileRequestStatus;

  public constructor(
    data: FinderProfileRequestListItemData | FinderProfileRequestData,
  ) {
    super([data], [data.customerId]);
    this.profileId = data.profileId;
    this.customerId = data.customerId;
    this.customer = Customer.ofId(data.customerId);
    this.domain = data.domain;
    this.createdAt = DateTime.fromISO(data.createdOn);
    this.resultAt = data.resultOn ? DateTime.fromISO(data.resultOn) : undefined;
    this.status = data.status;
    this.url = data.domain.startsWith("https://")
      ? data.domain
      : `https://${data.domain}`;
  }
}

export class FinderProfileRequestDetailed extends classes(
  FinderProfileRequestCommon,
  DataModel<FinderProfileRequestData>,
) {
  public readonly customer: Customer;

  public constructor(data: FinderProfileRequestData) {
    super([data], [data]);
    this.customer = Customer.ofId(data.customerId);
  }
}

export class FinderProfileRequestListItem extends classes(
  FinderProfileRequestCommon,
  DataModel<FinderProfileRequestListItemData>,
) {
  public constructor(data: FinderProfileRequestListItemData) {
    super([data], [data]);
  }
}

export class FinderProfileRequestListQuery extends ListQueryModel<FinderProfileRequestListModelQueryData> {
  public constructor(query: FinderProfileRequestListModelQueryData = {}) {
    super(query);
  }

  public refine(query: FinderProfileRequestListModelQueryData) {
    return new FinderProfileRequestListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.finderProfileRequest.list();

    return new FinderProfileRequestList(
      this.query,
      items.map((d) => new FinderProfileRequestListItem(d)),
    );
  }, [this.queryId, "finderProfileRequest"]);
}

export class FinderProfileRequestList extends classes(
  FinderProfileRequestListQuery,
  ListDataModel<FinderProfileRequestListItem>,
) {
  public constructor(
    query: FinderProfileRequestListModelQueryData,
    items: FinderProfileRequestListItem[],
  ) {
    super([query], [items, items.length]);
  }
}
